import { useEffect, useState } from "react";
import {
  Key, Copy, CheckCircle, Warning, Trash, Plus, PencilSimple,
  Prohibit, ArrowCounterClockwise, Buildings,
} from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/utils-extra";

const ROLE_COLOR = {
  super_admin: "bg-[#0F172A]/10 text-[#0F172A]",
  owner: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  hr: "bg-[#0284C7]/10 text-[#0284C7]",
  manager: "bg-[#16A34A]/10 text-[#16A34A]",
  employee: "bg-[#475569]/10 text-[#475569]",
};
const TENANT_ROLES = [
  { value: "owner", label: "Owner" },
  { value: "hr", label: "HR" },
  { value: "manager", label: "Manager" },
  { value: "employee", label: "Employee" },
];
const NONE = "__none__";
const emptyForm = { name: "", email: "", role: "employee", department: NONE, position_id: NONE, branch_id: NONE };

export default function Users({ embedded = false }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [list, setList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [resetInfo, setResetInfo] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [suspendUser, setSuspendUser] = useState(null);
  const [busy, setBusy] = useState(false);

  // create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newBranch, setNewBranch] = useState("");

  const reload = () => api.get("/users").then(({ data }) => setList(data)).catch(() => {});
  const reloadBranches = () => api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});

  useEffect(() => {
    reload();
    if (!isSuper) {
      api.get("/positions").then(({ data }) => setPositions(data)).catch(() => {});
      reloadBranches();
      api.get("/departments").then(({ data }) => setDepartments((data || []).filter((d) => d.active !== false).map((d) => d.name))).catch(() => {});
    }
  }, [isSuper]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setNewBranch(""); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      name: u.name, email: u.email, role: u.role,
      department: u.department || NONE, position_id: u.position_id || NONE, branch_id: u.branch_id || NONE,
    });
    setNewBranch("");
    setDialogOpen(true);
  };

  const payloadFromForm = () => ({
    name: form.name.trim(),
    email: form.email.trim(),
    role: form.role,
    department: form.department === NONE ? null : form.department,
    position_id: form.position_id === NONE ? null : form.position_id,
    branch_id: form.branch_id === NONE ? null : form.branch_id,
  });

  const save = async () => {
    setBusy(true);
    try {
      if (editId) {
        const p = payloadFromForm();
        delete p.email; // email is immutable here
        await api.put(`/users/${editId}`, p);
        toast.success("User updated");
        setDialogOpen(false);
        reload();
      } else {
        const { data } = await api.post("/users", payloadFromForm());
        setDialogOpen(false);
        reload();
        if (data.temp_password) setCreatedInfo(data);
        else toast.success("User created");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addBranch = async () => {
    const name = newBranch.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/branches", { name });
      setNewBranch("");
      await reloadBranches();
      setForm((f) => ({ ...f, branch_id: data.id }));
      toast.success(`Branch "${name}" added`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReset = async () => {
    if (!confirmReset) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${confirmReset.id}/reset-password`, {});
      setConfirmReset(null);
      setResetInfo(data);
      reload();
    } catch (e) { toast.error(formatApiError(e)); setConfirmReset(null); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!delUser) return;
    try {
      await api.delete(`/users/${delUser.id}`);
      toast.success("User deleted");
      setDelUser(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); setDelUser(null); }
  };

  const doSuspendToggle = async () => {
    if (!suspendUser) return;
    const u = suspendUser;
    const action = u.status === "suspended" ? "reactivate" : "suspend";
    try {
      await api.post(`/users/${u.id}/${action}`);
      toast.success(action === "suspend" ? "User suspended" : "User reactivated");
      setSuspendUser(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); setSuspendUser(null); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy it manually");
    }
  };

  const isSelf = (u) => u.id === user?.id;
  const canActOn = (u) => !isSelf(u) && (isSuper ? u.role !== "super_admin" : true);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="small-label">{isSuper ? "Platform" : "Access & Security"}</div>
          <h1 className={`font-display font-semibold mt-1 ${embedded ? "text-xl" : "text-3xl"}`}>
            {isSuper ? "Platform Users" : embedded ? "User Accounts" : "Users"}
          </h1>
          <p className="text-[#475569] text-sm mt-1">
            {isSuper
              ? "Internal platform accounts only (no tenant). Tenant users are managed inside each Tenant Detail workspace."
              : embedded
                ? "All login accounts in your organization, including accounts not linked to an employee. To manage a specific person's login, use the employee's profile › User Access tab."
                : "Create users, assign roles, departments, positions and branches, reset passwords, and suspend access — all within your organization."}
          </p>
        </div>
        {!isSuper && (
          <Button onClick={openCreate} data-testid="new-user-button" className="bg-[#0EA5E9] hover:bg-[#0284C7] shrink-0">
            <Plus size={18} className="mr-1" /> New User
          </Button>
        )}
      </div>

      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-0 divide-y divide-[#E2E8F0]">
          {list.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-4 p-4 flex-wrap" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-semibold text-sm shrink-0">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {u.name}
                    {isSelf(u) && <span className="text-xs text-[#94A3B8]">(you)</span>}
                  </div>
                  <div className="text-xs text-[#475569] truncate font-mono-data">{u.email}</div>
                  {!isSuper && (u.department || u.position_name || u.branch_name) && (
                    <div className="text-xs text-[#94A3B8] truncate mt-0.5">
                      {[u.position_name, u.department, u.branch_name].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {u.status === "suspended" && (
                  <Badge className="bg-[#DC2626]/10 text-[#DC2626] border-0 gap-1" data-testid={`user-suspended-${u.id}`}>
                    <Prohibit size={12} /> Suspended
                  </Badge>
                )}
                {u.must_change_password && u.status !== "suspended" && (
                  <Badge className="bg-[#D97706]/10 text-[#D97706] border-0 gap-1" data-testid={`user-pending-${u.id}`}>
                    <Warning size={12} /> Pending change
                  </Badge>
                )}
                <Badge className={`${ROLE_COLOR[u.role] || ROLE_COLOR.employee} border-0 capitalize`} data-testid={`user-role-${u.id}`}>
                  {u.role?.replace("_", " ")}
                </Badge>

                {!isSuper && canActOn(u) && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <PencilSimple size={14} className="mr-1" /> Edit
                  </Button>
                )}
                {!isSuper && canActOn(u) && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setSuspendUser(u)}
                    data-testid={`toggle-suspend-${u.id}`}
                    className={u.status === "suspended" ? "text-[#16A34A] hover:text-[#16A34A]" : "text-[#D97706] hover:text-[#D97706]"}
                  >
                    {u.status === "suspended"
                      ? <><ArrowCounterClockwise size={14} className="mr-1" /> Reactivate</>
                      : <><Prohibit size={14} className="mr-1" /> Suspend</>}
                  </Button>
                )}
                {canActOn(u) && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmReset(u)} data-testid={`reset-password-${u.id}`}>
                    <Key size={14} className="mr-1" /> Reset
                  </Button>
                )}
                {canActOn(u) && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setDelUser(u)}
                    data-testid={`delete-user-${u.id}`}
                    className="text-[#DC2626] hover:text-[#DC2626]"
                  >
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="p-8 text-center text-sm text-[#475569]">No users found.</div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit user */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="small-label">Full name</Label>
                <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="user-name-input" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="small-label">Email</Label>
                <Input className="mt-1.5" type="email" value={form.email} disabled={!!editId}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="user-email-input" placeholder="jane@company.com" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="small-label">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TENANT_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="small-label">Department</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-department-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="small-label">Position</Label>
                <Select value={form.position_id} onValueChange={(v) => setForm({ ...form, position_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-position-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="small-label">Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-branch-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {branches.filter((b) => b.active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Inline branch creation so branch assignment is functional immediately */}
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3">
              <div className="flex items-center gap-2 text-xs text-[#475569] mb-2">
                <Buildings size={14} /> Add a new branch
              </div>
              <div className="flex items-center gap-2">
                <Input value={newBranch} onChange={(e) => setNewBranch(e.target.value)}
                       placeholder="e.g. Downtown Office" data-testid="inline-branch-input"
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBranch(); } }} />
                <Button type="button" variant="outline" onClick={addBranch} disabled={!newBranch.trim()} data-testid="add-branch-button">
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {!editId && (
              <p className="text-xs text-[#475569]">A temporary password will be generated. The user must change it on first login.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name.trim() || (!editId && !form.email.trim())}
                    data-testid="save-user-button" className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              {busy ? "Saving…" : editId ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm suspend/reactivate */}
      <Dialog open={!!suspendUser} onOpenChange={(o) => !o && setSuspendUser(null)}>
        <DialogContent className="max-w-md" data-testid="suspend-confirm-dialog">
          <DialogHeader><DialogTitle>
            {suspendUser?.status === "suspended" ? "Reactivate user?" : "Suspend user?"}
          </DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            {suspendUser?.status === "suspended"
              ? <>Restore access for <span className="font-medium">{suspendUser?.email}</span>. They can log in again.</>
              : <>Suspend <span className="font-medium">{suspendUser?.email}</span>. They will be logged out and blocked from signing in until reactivated.</>}
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendUser(null)}>Cancel</Button>
            <Button onClick={doSuspendToggle} data-testid="confirm-suspend-button"
                    className={suspendUser?.status === "suspended" ? "bg-[#16A34A] hover:bg-[#15803D]" : "bg-[#D97706] hover:bg-[#B45309]"}>
              {suspendUser?.status === "suspended" ? "Reactivate" : "Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm reset */}
      <Dialog open={!!confirmReset} onOpenChange={(o) => !o && setConfirmReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset password?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            This generates a new temporary password for <span className="font-medium">{confirmReset?.email}</span>.
            They must change it on their next login. Their current password stops working immediately.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReset(null)}>Cancel</Button>
            <Button onClick={doReset} disabled={busy} data-testid="confirm-reset-button" className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              {busy ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#DC2626]"><Trash size={20} /> Delete user</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            Permanently delete <span className="font-semibold">{delUser?.email}</span>? They lose access immediately. This cannot be undone.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)}>Cancel</Button>
            <Button onClick={doDelete} data-testid="confirm-delete-user-button" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password (reset) */}
      <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent className="max-w-md" data-testid="reset-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <CheckCircle size={20} weight="duotone" color="#16A34A" /> Temporary password created
          </DialogTitle></DialogHeader>
          <Credentials info={resetInfo} copy={copy} testid="reset-temp-password" />
          <DialogFooter><Button onClick={() => setResetInfo(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials (create) */}
      <Dialog open={!!createdInfo} onOpenChange={(o) => !o && setCreatedInfo(null)}>
        <DialogContent className="max-w-md" data-testid="created-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <CheckCircle size={20} weight="duotone" color="#16A34A" /> User created
          </DialogTitle></DialogHeader>
          <Credentials info={createdInfo} copy={copy} testid="created-temp-password" />
          <DialogFooter><Button onClick={() => setCreatedInfo(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Credentials({ info, copy, testid }) {
  return (
    <>
      <DialogDescription className="text-sm text-[#475569]">
        Share these credentials securely. The user must set a new password at next login.
      </DialogDescription>
      <div className="rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] p-4 space-y-3">
        <div>
          <div className="small-label mb-1">Email</div>
          <div className="font-mono-data text-sm">{info?.email}</div>
        </div>
        <div>
          <div className="small-label mb-1">Temporary password</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono-data text-sm bg-white border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid={testid}>
              {info?.temp_password}
            </code>
            <Button variant="outline" size="sm" onClick={() => copy(info?.temp_password)} data-testid="copy-temp-password">
              <Copy size={14} />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
